import React, { useMemo } from "react";
import { formatDateDMY } from "./timeUtils";

const timeToMinutes = (time) => {
  const [hours, minutes] = String(time || "00:00").split(":").map(Number);
  return hours * 60 + minutes;
};

const minutesToTime = (minutes) =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(
    minutes % 60
  ).padStart(2, "0")}`;

const splitAvailabilityIntoHourSlots = (availability) =>
  (availability?.availableRooms || []).flatMap((room) =>
    (room.availableSlots || []).flatMap((availableSlot) => {
      const endMinutes = timeToMinutes(availableSlot.endTime);
      const slots = [];

      for (
        let startMinutes = timeToMinutes(availableSlot.startTime);
        startMinutes < endMinutes;
        startMinutes += 60
      ) {
        const slotEndMinutes = Math.min(startMinutes + 60, endMinutes);
        slots.push({
          roomId: room.roomId,
          floor: room.floor,
          roomOrder: room.order,
          startMinutes,
          startTime: minutesToTime(startMinutes),
          endTime: minutesToTime(slotEndMinutes),
        });
      }

      return slots;
    })
  );

const AvailableTimeSlots = ({
  availability,
  selectedRoomId,
  selectedDate,
  selectedStartTime,
  selectedEndTime,
  onSelectSlot,
}) => {
  const slots = useMemo(
    () =>
      splitAvailabilityIntoHourSlots(availability).sort(
        (a, b) =>
          a.startMinutes - b.startMinutes || a.roomOrder - b.roomOrder
      ),
    [availability]
  );

  const slotsByFloor = useMemo(() => {
    const groups = slots.reduce((result, slot) => {
      const floor = Number(slot.floor) || 0;
      if (!result[floor]) result[floor] = [];
      result[floor].push(slot);
      return result;
    }, {});

    return Object.entries(groups).sort(([floorA], [floorB]) =>
      Number(floorA) - Number(floorB)
    );
  }, [slots]);

  if (!availability) return null;

  return (
    <section className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:shadow-black/20 md:p-5">
      <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-slate-100">
            ช่วงเวลาว่างวันนี้ ({formatDateDMY(availability.dateKey)})
          </h2>
        </div>
        <span className="text-xs text-gray-500 dark:text-slate-400">
          พักเที่ยง {availability.lunchBreak?.label || "12:00 - 13:00"}
        </span>
      </div>

      {slots.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500 dark:border-slate-700 dark:text-slate-400">
          วันนี้ไม่มีช่วงเวลาว่าง
        </div>
      ) : (
        <div className="space-y-2.5">
          {slotsByFloor.map(([floor, floorSlots]) => (
            <div
              key={floor}
              className="flex flex-col gap-2 rounded-xl border border-gray-100 bg-gray-50/70 p-2.5 dark:border-slate-800 dark:bg-slate-950/40 sm:flex-row sm:items-center"
            >
              <div className="shrink-0 rounded-lg bg-indigo-50 px-3 py-2 text-center text-sm font-semibold text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300">
                ชั้น {floor}
              </div>
              <div className="flex flex-wrap gap-2">
                {floorSlots.map((slot) => {
                  const isSelected =
                    String(slot.roomId) === String(selectedRoomId) &&
                    selectedDate === availability.dateKey &&
                    selectedStartTime === slot.startTime &&
                    selectedEndTime === slot.endTime;

                  return (
                    <button
                      key={`${slot.roomId}-${slot.startTime}-${slot.endTime}`}
                      type="button"
                      onClick={() => onSelectSlot?.(slot)}
                      className={[
                        "rounded-lg border px-3 py-2 text-sm font-semibold transition",
                        isSelected
                          ? "border-indigo-500 bg-indigo-600 text-white shadow-sm"
                          : "border-white bg-white text-gray-800 hover:border-indigo-300 hover:text-indigo-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-indigo-400 dark:hover:text-indigo-300",
                      ].join(" ")}
                      aria-label={`เลือกชั้น ${floor} เวลา ${slot.startTime} ถึง ${slot.endTime}`}
                    >
                      {slot.startTime} - {slot.endTime}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default AvailableTimeSlots;
